import { useEffect, useState, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, Globe, Check, Shield } from 'lucide-react';
import { BRAND } from '../../data/site';
import { useT } from '../../i18n/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

const ADMIN_REVEAL_KEY = 'mbk_admin_revealed';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [adminRevealed, setAdminRevealed] = useState(() => sessionStorage.getItem(ADMIN_REVEAL_KEY) === '1');
  const langRef = useRef(null);
  const location = useLocation();
  const { t, code, setCode, meta, languages } = useT();
  const { theme, toggle } = useTheme();

  const links = [
    { label: t('nav.about'), to: '/about', testid: 'about' },
    { label: t('nav.strategy'), to: '/strategy', testid: 'strategy' },
    { label: t('nav.ventures'), to: '/ventures', testid: 'ventures' },
    { label: t('nav.services'), to: '/services', testid: 'services' },
    { label: t('nav.security'), to: '/security', testid: 'security' },
    { label: t('nav.news'), to: '/news', testid: 'news' },
    { label: t('nav.media'), to: '/media', testid: 'media' },
    { label: t('nav.contact'), to: '/contact', testid: 'contact' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setLangOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Hidden admin reveal: Ctrl+Shift+A toggles the Admin link in the navbar
  useEffect(() => {
    const onKey = (e) => {
      const key = (e.key || '').toLowerCase();
      if (e.ctrlKey && e.shiftKey && key === 'a') {
        e.preventDefault();
        setAdminRevealed((prev) => {
          const next = !prev;
          if (next) sessionStorage.setItem(ADMIN_REVEAL_KEY, '1');
          else sessionStorage.removeItem(ADMIN_REVEAL_KEY);
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass border-b border-subtle' : 'bg-transparent'
      }`}
      style={scrolled ? { backgroundColor: 'var(--bg-surface-2)' } : {}}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
            <img src={BRAND.logo} alt="MBK Holding" className="h-10 md:h-12 w-auto transition-transform group-hover:scale-105" />
            <div className="hidden md:flex flex-col leading-tight">
              <span className="font-display text-prim text-base tracking-tight">MBK Holding</span>
              <span className="font-mono text-[10px] tracking-[0.25em] text-brand-teal-light uppercase">Doha · Est. Quietly</span>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-6">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.testid}`}
                className={({ isActive }) =>
                  `relative font-body text-sm tracking-wide transition-colors hover-line ${
                    isActive ? 'text-brand-teal-light' : 'text-sec hover:text-prim'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* Hidden Admin link — revealed via Ctrl+Shift+A */}
            {adminRevealed && (
              <Link
                to="/admin/login"
                data-testid="nav-admin-link"
                title={t('nav.admin') || 'Admin'}
                className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-teal-light/40 bg-brand-teal/5 text-brand-teal-light hover:bg-brand-teal/15 transition-all"
                style={{ animation: 'fade-up 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }}
              >
                <Shield size={14} />
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase">{t('nav.admin') || 'Admin'}</span>
                <span className="absolute inset-0 rounded-full ring-2 ring-brand-teal-light/30 animate-ping pointer-events-none" style={{ animationIterationCount: 2 }} />
              </Link>
            )}

            {/* Theme toggle */}
            <button
              data-testid="theme-toggle"
              onClick={toggle}
              aria-label="Toggle theme"
              className="p-2.5 rounded-full border border-subtle hover:border-brand-teal-light/50 text-sec hover:text-prim transition-colors"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {/* Language switcher */}
            <div ref={langRef} className="relative">
              <button
                data-testid="lang-toggle"
                onClick={() => setLangOpen((s) => !s)}
                className="p-2.5 rounded-full border border-subtle hover:border-brand-teal-light/50 text-sec hover:text-prim transition-colors flex items-center gap-2"
                aria-label="Change language"
                title="Change language"
              >
                <Globe size={16} />
                <span className="hidden md:inline text-xs font-mono uppercase tracking-wider">{meta.code}</span>
              </button>
              {langOpen && (
                <div data-testid="lang-menu" className="absolute right-0 mt-2 w-56 glass rounded-md border border-subtle py-2 shadow-xl max-h-[70vh] overflow-y-auto z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      data-testid={`lang-opt-${l.code}`}
                      onClick={() => { setCode(l.code); setLangOpen(false); }}
                      className={`w-full px-4 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-brand-teal/10 ${l.code === code ? 'text-brand-teal-light' : 'text-prim'}`}
                    >
                      <span className="flex items-center gap-3">
                        <span className="text-base">{l.flag}</span>
                        <span className="font-display text-sm">{l.native}</span>
                        <span className="text-xs text-muted">{l.label}</span>
                      </span>
                      {l.code === code && <Check size={14} />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link to="/contact" data-testid="nav-cta" className="hidden md:inline-flex btn-primary text-sm py-2 px-4">
              {t('nav.inquire')}
            </Link>

            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((s) => !s)}
              className="xl:hidden p-2 text-prim"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="xl:hidden glass border-t border-subtle">
          <div className="max-w-[1400px] mx-auto px-6 py-6 flex flex-col gap-4">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-mobile-link-${link.testid}`}
                className={({ isActive }) =>
                  `font-display text-xl tracking-tight ${isActive ? 'text-brand-teal-light' : 'text-prim'}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="nav-mobile-cta" className="btn-primary mt-2 self-start">
              {t('nav.inquire')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
